import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { ActionAPI, CheckboxAPI, FormAPI, InputAPI, ModalProps, ModalService, RadioGroupAPI, SelectableItem, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { Inject } from '@odinjs/odin';

import BaseController from '../../../BaseController';
import { FragmentNames, ScoreMorseViewerAliases } from '../../../../codegen';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { ScoreMorseResponse } from '../../score-types';
import { ApplicationRoute } from '../../../../constants/routes';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ScoreAndIndexesService, ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { MorseRadioGroupExpressions, MorseRadioGroupId } from '../radio-group-data/morse-radio-group-expressions';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { HealthProfessionalUser } from '../../../../app/authentication';

@Controller({ fragment: FragmentNames.SCORE_MORSE_VIEWER })
export default class ScoreMorseViewerController extends BaseController {

  @Inject(ScoreMorseViewerAliases.FORM_SCORE_MORSE_VIEWER)
  private formAPI!: FormAPI;

  @Inject(ScoreMorseViewerAliases.INPUT_EVATUALION_DATE)
  private setupDateInputApi!: InputAPI;

  @Inject(ScoreMorseViewerAliases.INPUT_PROFESSIONAL)
  private inputProfessionalAPI!: InputAPI;

  @Inject(ScoreMorseViewerAliases.TEXT_PUNCTUATION)
  private textPunctuationApi!: TextAPI;

  @Inject(ScoreMorseViewerAliases.TEXT_DS_PUNCTUATION)
  private textDsPunctuationApi!: TextAPI;

  @Inject(ScoreMorseViewerAliases.ACTION_INACTIVATE_SCORE_MORSE)
  private actionInactivateAPI!: ActionAPI;

  @Inject(ScoreMorseViewerAliases.ACTION_NEW_SCORE_MORSE)
  private actionNewAPI!: ActionAPI;

  @Inject(ScoreMorseViewerAliases.ACTION_REFERENCE_SCORE_MORSE)
  private actionReferenceAPI!: ActionAPI;

  @Inject(ScoreMorseViewerAliases.CHECKBOX_FALL_HISTORY_OPTION)
  private checkboxFallHistoryOption!: CheckboxAPI;

  @Inject(ScoreMorseViewerAliases.CHECKBOX_ENDOVENOUS_DEVICE_OPTION)
  private checkboxHeparinOption!: CheckboxAPI;

  @Inject(ScoreMorseViewerAliases.CHECKBOX_HAS_COMORBIDITY_OPTION)
  private checkboxDiaSecondaryOption!: CheckboxAPI;

  @Inject(ScoreMorseViewerAliases.RADIO_GROUP_AMBULATORY_AID)
  private radioGroupHelpDeambularApi!: RadioGroupAPI;

  @Inject(ScoreMorseViewerAliases.RADIO_GROUP_GAIT_CHARACTERISTIC)
  private radioGroupWalkingModeApi!: RadioGroupAPI;

  @Inject(ScoreMorseViewerAliases.RADIO_GROUP_MENTAL_STATE)
  private radioGroupMentalStateApi!: RadioGroupAPI;

  @OnMount({ target: ScoreMorseViewerAliases.LAYOUT_SCORE_MORSE_VIEWER })
  handleOnMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
    ScoreListContext.currentTab = ScoreType.SCORE_MORSE
  }

  @OnMount({ target: ScoreMorseViewerAliases.FORM_SCORE_MORSE_VIEWER })
  handleOnMountForm(): void {
    this.formAPI.setLoading(true)
    this.fetchScoreMorse()
  }

  @OnClick({ target: ScoreMorseViewerAliases.ACTION_INACTIVATE_SCORE_MORSE })
  handleInactivate(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-inactivate',
      id: 'score-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      idScoreIndexes,
    } as Record<string, Value>;

    ModalService.openDefault(modalProperties);
  }

  @OnClick({ target: ScoreMorseViewerAliases.ACTION_REFERENCE_SCORE_MORSE })
  handleClickReference(): void {
    this.getReference();
  }

  @OnClick({ target: ScoreMorseViewerAliases.ACTION_NEW_SCORE_MORSE })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.SCORE_MORSE_CREATE, {
      encounterId,
      patientId,
    });
  }

  private scaleRadioGroup(expressionId: number, radioGroup: RadioGroupAPI, type: string): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        if (response) {
          const responseBody = response.body as SelectableItem[];
          radioGroup.setData(responseBody);

          const scoreMorse = this.context.get('scoreMorse') as ScoreMorseResponse;

          if (type === MorseRadioGroupExpressions.HELP_DEAMBULAR) {
            const helpDeambular = responseBody.filter(({ key }) => scoreMorse?.helpDeambular === Number(key))[0];
            radioGroup.setValue(helpDeambular);
          }

          if (type === MorseRadioGroupExpressions.MENTAL_STATE) {
            const mentalState = responseBody.filter(({ key }) => scoreMorse?.mentalState === Number(key))[0];
            radioGroup.setValue(mentalState);
          }

          if (type === MorseRadioGroupExpressions.WALKING_MODE) {
            const walkingMode = responseBody.filter(({ key }) => scoreMorse?.walkingMode === Number(key))[0];
            radioGroup.setValue(walkingMode);
          }
        }
      }).catch((error) => {
        this.showToast(error?.body.errorMessage, ToastLevel.ERROR)
      });
  }

  private getReference(): void {
    const scoreType = 'morse';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType,
    }

    ModalService.openFullscreen(modalProperties);
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-nas-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private fetchScoreMorse(): Promise<void> {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    const allowInfo = InformationBarUtils.allowInfo && ScoreAndIndexesService.isAllowEdit;

    return HttpService.get(Endpoint.SCORE_MORSE, {
      sequenceNumber: idScoreIndexes
    }).then(response => {
      const record = response.body as ScoreMorseResponse;
      const user = foundationContext.getUser() as HealthProfessionalUser;
      const dateEvaluation = DateFormatterUtil.timeStamp(String(record?.dateEvaluation));
      const diaSecondary = record.diaSecondary;
      const heparin = record.heparin;
      const histFall = record.histFall;
      const qtPunctuation = record.punctuation?.id || '0';
      const dsPunctuation = record.punctuation?.description || '';
      const showInactivate = record.professional?.id === user.physicalPersonId && Boolean(record.showInactivate) && allowInfo;

      this.context.set({ scoreMorse: record });

      this.inputProfessionalAPI.setValue(record.professional?.description as string);
      this.setupDateInputApi.setValue(dateEvaluation);
      this.textPunctuationApi.setValue(qtPunctuation as string);
      this.textDsPunctuationApi.setValue(dsPunctuation);
      this.checkboxFallHistoryOption.setValue(BooleanUtils.isTrueValue(histFall))
      this.checkboxHeparinOption.setValue(BooleanUtils.isTrueValue(heparin))
      this.checkboxDiaSecondaryOption.setValue(BooleanUtils.isTrueValue(diaSecondary))
      this.actionInactivateAPI.setEnabled(showInactivate);

      this.scaleRadioGroup(MorseRadioGroupId.HELP_DEAMBULAR, this.radioGroupHelpDeambularApi, MorseRadioGroupExpressions.HELP_DEAMBULAR);
      this.scaleRadioGroup(MorseRadioGroupId.WALKING_MODE, this.radioGroupWalkingModeApi, MorseRadioGroupExpressions.WALKING_MODE);
      this.scaleRadioGroup(MorseRadioGroupId.MENTAL_STATE, this.radioGroupMentalStateApi, MorseRadioGroupExpressions.MENTAL_STATE);
    })
      .catch((error) => {
        this.showToast(error?.body.errorMessage, ToastLevel.ERROR)
      })
      .finally(() => {
        this.formAPI.setLoading(false);
        this.actionReferenceAPI.setEnabled(allowInfo);
        this.actionNewAPI.setEnabled(allowInfo);
      });
  }

  @OnUnmount(ScoreMorseViewerAliases.LAYOUT_SCORE_MORSE_VIEWER)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_MORSE;
  }
}
